import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { motion } from "motion/react";
import { cn } from "./cn";
import { Count } from "./Count";
import { spring } from "./motion";

export type SegmentedOption<T extends string> = { value: T; label: ReactNode; count?: number; tone?: "bad" };

export type SegmentedProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  className?: string;
  "aria-label"?: string;
};

export function Segmented<T extends string>({ value, onChange, options, className, "aria-label": ariaLabel }: SegmentedProps<T>) {
  const group = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(e: KeyboardEvent, index: number) {
    const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = (index + step + options.length) % options.length;
    onChange(options[next]!.value);
    refs.current[next]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn("inline-flex max-w-full gap-1 overflow-x-auto [scrollbar-width:none]", className)}
    >
      {options.map((o, i) => {
        const active = o.value === value;
        return (
          <motion.button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            whileTap={{ scale: 0.97 }}
            transition={spring}
            className={cn(
              "relative inline-flex h-9 shrink-0 items-center gap-2 rounded-control px-3.5 text-sm font-semibold transition-[background-color,color] duration-150",
              active ? "text-primary-ink" : "text-muted hover:bg-ink/[0.04] hover:text-ink",
            )}
          >
            {active ? (
              <motion.span
                layoutId={`seg-${group}`}
                className="absolute inset-0 rounded-control bg-primary-soft"
                transition={spring}
              />
            ) : null}
            <span className="relative">{o.label}</span>
            {o.count !== undefined ? <Count value={o.count} active={active} tone={o.tone === "bad" && o.count ? "bad" : undefined} /> : null}
          </motion.button>
        );
      })}
    </div>
  );
}
